import React from 'react';
import {
  Card, CardContent, Typography, Box, Chip, Button,
  LinearProgress, Link, Tooltip, Grid, Dialog, DialogTitle,
  DialogContent, DialogActions,
  Avatar, IconButton, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Router, AccessTime, NetworkCheck,
  Wifi, WifiOff, Storage, PowerOff,
  Bolt, DeviceThermostat, History,
  Assessment, CheckCircleOutline, ErrorOutline,
  Inventory, Computer as ComputerIcon,
  ArrowUpward, ArrowDownward
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';
import { formatDistanceToNow, format, parseISO, isAfter, isToday, isTomorrow, isValid } from 'date-fns';
import { GlassmorphicCard } from '../styles/sharedStyles';
import axios from 'axios';

import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';

// Available VM icons (same as VirtualMachines.js)
const VM_ICONS = [
  { id: 'linux', name: 'Linux', path: '/linux.png' },
  { id: 'ubuntu', name: 'Ubuntu', path: '/ubuntu-linux.png' },
  { id: 'windows10', name: 'Windows 10', path: '/windows-10.png' },
  { id: 'windows11', name: 'Windows 11', path: '/windows-11.png' },
];

// Helper to load VM icons from backend API
const loadVMIcons = async (token, apiBaseUrl) => {
  try {
    const response = await axios.get(`${apiBaseUrl}/api/vms/icons`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    const iconAssignments = response.data.icon_assignments || [];
    const iconsMap = {};
    iconAssignments.forEach(assignment => {
      iconsMap[assignment.vm_id] = assignment.icon_id;
    });
    return iconsMap;
  } catch (error) {
    console.error('Failed to load VM icons:', error);
    return {};
  }
};

const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.7; }
  100% { transform: scale(1); opacity: 1; }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

const MetricGlow = styled(Box)(({ color }) => ({
  height: '3px',
  width: '100%',
  borderRadius: '2px',
  background: color,
  boxShadow: `0 0 12px ${color}`,
  marginTop: '8px',
  opacity: 0.6,
}));

const StatPill = styled(Box)(({ theme, color }) => ({
  background: `rgba(${color}, 0.1)`,
  border: `1px solid rgba(${color}, 0.2)`,
  borderRadius: '12px',
  padding: '6px 12px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  transition: 'all 0.3s ease',
  flexShrink: 0,
  minWidth: '100px',
  boxSizing: 'border-box',
  '&:hover': {
    backgroundColor: `rgba(${color}, 0.15)`,
    borderColor: `rgba(${color}, 0.4)`,
    transform: 'translateY(-2px)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px 12px',
    borderRadius: '10px',
    minWidth: '100px',
  },
}));

const MobileScrollRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  overflowX: 'auto',
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(0.5),
  marginLeft: theme.spacing(1),
  WebkitOverflowScrolling: 'touch',
  scrollSnapType: 'x mandatory',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(0, -2),
    marginLeft: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    '& > *': {
      flex: '0 0 85%',
      scrollSnapAlign: 'start',
      minWidth: '280px',
    },
  },
  [theme.breakpoints.up('sm')]: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(3),
    overflowX: 'visible',
    paddingBottom: 0,
    margin: 0,
    marginLeft: theme.spacing(1),
    paddingLeft: 0,
    paddingRight: 0,
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
}));

const DiskPoolsScrollRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  overflowX: 'auto',
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(0.5),
  marginLeft: theme.spacing(1),
  WebkitOverflowScrolling: 'touch',
  scrollSnapType: 'x mandatory',
  '&::-webkit-scrollbar': { display: 'none' },
  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(0, -2),
    marginLeft: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    '& > *': {
      flex: '0 0 85%',
      scrollSnapAlign: 'start',
      minWidth: '280px',
    },
  },
  [theme.breakpoints.up('sm')]: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: theme.spacing(2),
    overflowX: 'visible',
    paddingBottom: 0,
    margin: 0,
    marginLeft: theme.spacing(1),
    paddingLeft: 0,
    paddingRight: 0,
    '& > *': { minWidth: 0 },
  },
}));

const DiskBadge = styled(Box)(({ theme, temp, isProtected }) => {
  let color = '0, 255, 136'; // Default green
  if (temp > 45) color = '255, 68, 68'; // Red
  else if (temp > 38) color = '255, 235, 59'; // Yellow
  
  if (isProtected) {
    return {
      background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(255, 165, 0, 0.05))',
      border: '1px solid rgba(255, 215, 0, 0.3)',
      borderRadius: '12px',
      padding: '8px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '2px',
      minWidth: '70px',
      transition: 'all 0.3s ease',
      boxShadow: '0 0 15px rgba(255, 215, 0, 0.1)',
      '&:hover': {
        background: 'rgba(255, 215, 0, 0.18)',
        borderColor: 'rgba(255, 215, 0, 0.45)',
        boxShadow: '0 4px 20px rgba(255, 215, 0, 0.25)',
        transform: 'translateY(-3px)',
      },
    };
  }

  return {
    background: `linear-gradient(135deg, rgba(${color}, 0.1), rgba(${color}, 0.05))`,
    border: `1px solid rgba(${color}, 0.2)`,
    borderRadius: '12px',
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
    minWidth: '70px',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: `rgba(${color}, 0.12)`,
      borderColor: `rgba(${color}, 0.35)`,
      boxShadow: `0 4px 15px rgba(${color}, 0.15)`,
      transform: 'translateY(-3px)',
    },
  };
});

const PoolCard = styled(Box)(({ theme, type }) => {
  let border = '1px solid rgba(255, 255, 255, 0.05)';
  let bg = theme.palette.background.paper;
  let glow = 'none';

  if (type === 'protected') {
    border = '1px solid rgba(255, 215, 0, 0.3)';
    bg = `linear-gradient(135deg, rgba(255, 215, 0, 0.05), ${theme.palette.background.paper})`;
    glow = '0 0 20px rgba(255, 215, 0, 0.05)';
  } else if (type === 'system') {
    border = `1px solid ${theme.palette.primary.main}4d`;
    bg = `linear-gradient(135deg, ${theme.palette.primary.main}0d, ${theme.palette.background.paper})`;
  } else {
    // Purple for Stripe pools
    border = '1px solid rgba(156, 39, 176, 0.3)';
    bg = `linear-gradient(135deg, rgba(156, 39, 176, 0.05), ${theme.palette.background.paper})`;
  }

  return {
    padding: '20px',
    background: bg,
    borderRadius: `${(theme.custom?.cardRounding || 28) * 0.7}px`,
    border: border,
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
    minHeight: 320,
    boxShadow: glow,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
      borderColor: 'rgba(255, 255, 255, 0.15)',
    }
  };
});

const ShutdownButton = styled(Button)(({ theme }) => ({
  background: `${theme.palette.error.main}1a`,
  border: `1px solid ${theme.palette.error.main}4d`,
  borderRadius: '16px',
  color: theme.palette.error.main,
  fontWeight: 600,
  padding: '8px 16px',
  minHeight: '44px',
  minWidth: '140px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  backdropFilter: 'blur(10px)',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    background: `${theme.palette.error.main}33`,
    borderColor: theme.palette.error.main,
    boxShadow: `0 8px 32px ${theme.palette.error.main}33`,
    transform: 'translateY(-2px)',
  },
  '&:disabled': {
    opacity: 0.6,
    color: `${theme.palette.error.main}80`,
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: 36,
    minWidth: 'auto',
    padding: '6px 12px',
    borderRadius: 10,
    fontSize: '0.75rem',
    '& .MuiButton-startIcon': {
      marginRight: 4,
      '& > *:nth-of-type(1)': { fontSize: 16 },
    },
  },
}));

const StyledCard = styled(GlassmorphicCard)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const StatusIndicator = styled(Box)(({ isOnline }) => ({
  width: '12px',
  height: '12px',
  borderRadius: '50%',
  background: isOnline
    ? 'linear-gradient(135deg, #00ff88, #00d4aa)'
    : 'linear-gradient(135deg, #ff4444, #ff6b6b)',
  boxShadow: isOnline
    ? '0 0 20px rgba(0, 255, 136, 0.4)'
    : '0 0 20px rgba(255, 68, 68, 0.4)',
  animation: isOnline ? 'pulse 2s ease-in-out infinite' : 'none',
}));

const ModernChip = styled(Chip)(({ theme, isOnline, colorType }) => {
  let gradient;
  let border;
  let color;
  
  if (colorType === 'info') {
    gradient = 'linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(0, 153, 255, 0.1))';
    border = '1px solid rgba(0, 212, 255, 0.3)';
    color = '#00d4ff';
  } else if (colorType === 'success') {
    gradient = 'linear-gradient(135deg, rgba(0, 255, 136, 0.1), rgba(0, 212, 170, 0.1))';
    border = '1px solid rgba(0, 255, 136, 0.3)';
    color = '#00ff88';
  } else if (colorType === 'error' || !isOnline) {
    gradient = 'linear-gradient(135deg, rgba(255, 68, 68, 0.1), rgba(255, 107, 107, 0.1))';
    border = '1px solid rgba(255, 68, 68, 0.3)';
    color = '#ff4444';
  } else {
    gradient = 'linear-gradient(135deg, rgba(0, 255, 136, 0.1), rgba(0, 212, 170, 0.1))';
    border = '1px solid rgba(0, 255, 136, 0.3)';
    color = '#00ff88';
  }

  return {
    background: gradient,
    border: border,
    color: color,
    fontWeight: 600,
    fontSize: '0.875rem',
    minHeight: '40px',
    minWidth: '100px',
    padding: '8px 16px',
    borderRadius: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
    '&:hover': {
      transform: 'scale(1.02)',
      boxShadow: `0 4px 20px ${color}33`,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem',
      minHeight: 36,
      minWidth: 'auto',
      padding: '6px 10px',
      borderRadius: 10,
      '& .MuiChip-icon': {
        fontSize: 16,
      },
    },
  };
});

const UtilizationCard = styled(Box, { shouldForwardProp: (p) => p !== 'failover' })(({ theme, failover }) => ({
  background: failover
    ? 'linear-gradient(145deg, rgba(30, 8, 8, 0.95), rgba(60, 20, 20, 0.9))'
    : theme.palette.background.paper,
  borderRadius: `${(theme.custom?.cardRounding || 28) * 0.7}px`,
  padding: '20px',
  border: failover ? '2px solid rgba(244, 67, 54, 0.6)' : '1px solid rgba(255, 255, 255, 0.06)',
  boxShadow: failover ? '0 0 24px rgba(244, 67, 54, 0.2)' : 'none',
  backdropFilter: `blur(${(theme.custom?.glassBlur || 20) * 0.5}px)`,
  transition: 'all 0.3s ease',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  minHeight: 185,
  height: '100%',
  '&:hover': {
    backgroundImage: failover
      ? 'linear-gradient(rgba(244, 67, 54, 0.08), rgba(244, 67, 54, 0.05))'
      : 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
    borderColor: failover ? 'rgba(244, 67, 54, 0.8)' : 'rgba(255, 255, 255, 0.15)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: 14,
    borderRadius: `${(theme.custom?.cardRounding || 28) * 0.5}px`,
    minHeight: 165,
  },
}));

const UpcomingJobBadge = styled(Box)(({ theme, statusColor }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: '16px',
  padding: '10px 16px',
  minWidth: '240px',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
    borderColor: 'rgba(0, 212, 255, 0.3)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '4px',
    background: statusColor,
    boxShadow: `0 0 10px ${statusColor}`,
  }
}));

const StatusCard = ({ status, loading, error, onShutdown, shutdownLoading, onRefresh, apiBaseUrl, token }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [openShutdownDialog, setOpenShutdownDialog] = React.useState(false);
  const [tasks, setTasks] = React.useState([]);
  const [tasksLoading, setTasksLoading] = React.useState(false);
  const [powerData, setPowerData] = React.useState(null);
  const [powerLoading, setPowerLoading] = React.useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = React.useState(false);
  const [historyPeriod, setHistoryPeriod] = React.useState('24h');
  const [historyData, setHistoryData] = React.useState([]);
  const [networkHistory, setNetworkHistory] = React.useState([]);
  const [networkHistoryLoading, setNetworkHistoryLoading] = React.useState(false);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [chartPeriod, setChartPeriod] = React.useState('1h'); // '1h', '4h', '12h', '24h'
  const chartPeriodRef = React.useRef(chartPeriod);
  chartPeriodRef.current = chartPeriod;
  const [unifiOverview, setUnifiOverview] = React.useState(null);
  const [unifiPorts, setUnifiPorts] = React.useState(null);
  const [haStates, setHaStates] = React.useState({});
  const [cpuRamData, setCpuRamData] = React.useState(null);
  const [vmIcons, setVmIcons] = React.useState({});
  const scrollContainerRef = React.useRef(null);
  const isDraggingRef = React.useRef(false);
  const startXRef = React.useRef(0);
  const scrollLeftRef = React.useRef(0);

  // Handle horizontal scroll with mouse wheel
  const handleWheel = React.useCallback((e) => {
    if (scrollContainerRef.current && e.deltaY !== 0) {
      e.preventDefault();
      scrollContainerRef.current.scrollLeft += e.deltaY;
    }
  }, []);

  // Handle drag-to-scroll with mouse
  const handleMouseDown = React.useCallback((e) => {
    if (scrollContainerRef.current) {
      isDraggingRef.current = true;
      startXRef.current = e.pageX - scrollContainerRef.current.offsetLeft;
      scrollLeftRef.current = scrollContainerRef.current.scrollLeft;
      scrollContainerRef.current.style.cursor = 'grabbing';
    }
  }, []);

  const handleMouseMove = React.useCallback((e) => {
    if (!isDraggingRef.current || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startXRef.current) * 2; // Multiply for faster scroll
    scrollContainerRef.current.scrollLeft = scrollLeftRef.current - walk;
  }, []);

  const handleMouseUp = React.useCallback(() => {
    if (scrollContainerRef.current) {
      isDraggingRef.current = false;
      scrollContainerRef.current.style.cursor = 'grab';
    }
  }, []);

  // Attach non-passive wheel event listener
  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      container.addEventListener('mousedown', handleMouseDown);
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseup', handleMouseUp);
      container.addEventListener('mouseleave', handleMouseUp);
    }
    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
        container.removeEventListener('mousedown', handleMouseDown);
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mouseup', handleMouseUp);
        container.removeEventListener('mouseleave', handleMouseUp);
      }
    };
  }, [handleWheel, handleMouseDown, handleMouseMove, handleMouseUp]);

  const fetchTasks = React.useCallback(async () => {
    if (!token || !apiBaseUrl) return;
    try {
      setTasksLoading(true);
      const response = await axios.get(`${apiBaseUrl}/api/scheduler/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data.tasks || []);
    } catch (err) {
      console.error('Error fetching tasks in StatusCard:', err);
    } finally {
      setTasksLoading(false);
    }
  }, [apiBaseUrl, token]);

  const fetchPowerData = React.useCallback(async () => {
    if (!token || !apiBaseUrl) return;
    try {
      setPowerLoading(true);
      const response = await axios.get(`${apiBaseUrl}/api/homeassistant/power`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPowerData(response.data);
    } catch (err) {
      console.error('Error fetching power data in StatusCard:', err);
      setPowerData(null);
    } finally {
      setPowerLoading(false);
    }
  }, [apiBaseUrl, token]);

  const fetchHistoryData = React.useCallback(async (period = '24h') => {
    if (!token || !apiBaseUrl) return;
    try {
      setHistoryLoading(true);
      const sensorId = 'sensor.truenas_power_power';
      const response = await axios.get(`${apiBaseUrl}/api/homeassistant/history/${sensorId}?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Process data for Recharts
      if (Array.isArray(response.data)) {
        const processed = response.data
          .filter(entry => entry.state !== 'unknown' && entry.state !== 'unavailable')
          .map(entry => ({
            timestamp: new Date(entry.last_updated).getTime(),
            time: format(new Date(entry.last_updated), period === '24h' ? 'HH:mm' : 'MMM d HH:mm'),
            value: parseFloat(entry.state) || 0
          }));
        setHistoryData(processed);
      } else {
        setHistoryData([]);
      }
    } catch (err) {
      console.error('Error fetching power history in StatusCard:', err);
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [apiBaseUrl, token]);

  const fetchUnifiData = React.useCallback(async () => {
    if (!token || !apiBaseUrl) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [overviewRes, portsRes, haRes] = await Promise.all([
        axios.get(`${apiBaseUrl}/api/unifi/overview`, { headers }),
        axios.get(`${apiBaseUrl}/api/unifi/ports`, { headers }),
        axios.get(`${apiBaseUrl}/api/homeassistant/entities`, { headers }).catch(() => null),
      ]);

      setUnifiOverview(overviewRes.data || null);
      setUnifiPorts(portsRes.data || null);
      if (haRes && haRes.data && Array.isArray(haRes.data)) {
        const nextStates = {};
        haRes.data.forEach((entity) => {
          if (entity && entity.entity_id) {
            nextStates[entity.entity_id] = entity;
          }
        });
        setHaStates(nextStates);
      }
      
      // Update network history from database
      fetchNetworkHistory(chartPeriodRef.current);
    } catch (err) {
      console.error('Error fetching UniFi data in StatusCard:', err);
    }
  }, [apiBaseUrl, token]);

  const fetchCpuRam = React.useCallback(async () => {
    if (!token || !apiBaseUrl) return;
    try {
      const response = await axios.get(`${apiBaseUrl}/api/cpu-ram?t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000,
      });
      setCpuRamData(response.data);
    } catch (err) {
      console.error('Error fetching CPU/RAM data:', err);
    }
  }, [apiBaseUrl, token]);

  const fetchVMIcons = React.useCallback(async () => {
    if (!token || !apiBaseUrl) return;
    try {
      const response = await axios.get(`${apiBaseUrl}/api/vms/icons`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const iconAssignments = response.data.icon_assignments || [];
      const iconsMap = {};
      iconAssignments.forEach(assignment => {
        iconsMap[assignment.vm_id] = assignment.icon_id;
      });
      setVmIcons(iconsMap);
    } catch (err) {
      console.error('Error fetching VM icons:', err);
    }
  }, [apiBaseUrl, token]);

  const fetchNetworkHistory = React.useCallback(async (period = '1h') => {
    if (!token || !apiBaseUrl) return;
    let hours = 1;
    if (period === '4h') hours = 4;
    if (period === '12h') hours = 12;
    if (period === '24h') hours = 24;

    setNetworkHistoryLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/api/network-stats?wan=WAN1&hours=${hours}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      });
      // Transform to { time: timestamp, rx: rx_mbps, tx: tx_mbps }
      const data = response.data.map(p => ({
        time: new Date(p.timestamp).getTime(),
        rx: p.rx_mbps,
        tx: p.tx_mbps
      }));
      setNetworkHistory(data);
    } catch (err) {
      console.error('Error fetching network history:', err);
    } finally {
      setNetworkHistoryLoading(false);
    }
  }, [apiBaseUrl, token]);

  const handleHistoryClick = () => {
    setOpenHistoryDialog(true);
    fetchHistoryData(historyPeriod);
  };

  const handlePeriodChange = (newPeriod) => {
    setHistoryPeriod(newPeriod);
    fetchHistoryData(newPeriod);
  };

  // Integrate power (W) over time to get energy (kWh) from history — same method as HA, not derived from yearly
  const getPeriodKwhFromHistory = React.useCallback((data, period) => {
    if (!Array.isArray(data) || data.length < 2) return null;
    let kwh = 0;
    for (let i = 0; i < data.length - 1; i++) {
      const p1 = data[i].value || 0;
      const p2 = data[i + 1].value || 0;
      const hours = (data[i + 1].timestamp - data[i].timestamp) / (1000 * 3600);
      kwh += ((p1 + p2) / 2) * hours / 1000; // W * h → Wh, /1000 → kWh
    }
    return kwh;
  }, []);

  const getPeriodDays = (period) => {
    if (period === '24h') return 1;
    if (period === '7d') return 7;
    if (period === '30d') return 30;
    return 1;
  };

  React.useEffect(() => {
    fetchTasks();
    fetchPowerData();
    fetchUnifiData();
    fetchCpuRam();
    fetchVMIcons();
    const tasksInterval = setInterval(fetchTasks, 60000); // Every minute
    const powerInterval = setInterval(fetchPowerData, 30000); // Every 30 seconds
    const unifiInterval = setInterval(fetchUnifiData, 5000); // Every 5 seconds
    const cpuRamInterval = setInterval(fetchCpuRam, 5000); // Every 5 seconds
    const vmIconsInterval = setInterval(fetchVMIcons, 30000); // Every 30 seconds
    return () => {
      clearInterval(tasksInterval);
      clearInterval(powerInterval);
      clearInterval(unifiInterval);
      clearInterval(cpuRamInterval);
      clearInterval(vmIconsInterval);
    };
  }, [fetchTasks, fetchPowerData, fetchUnifiData, fetchCpuRam, fetchVMIcons]);

  const upcomingTasks = React.useMemo(() => {
    return tasks
      .filter(task => ['immich', 'truenas', 'rpi'].includes(task.service))
      .filter(task => task.next_run && !isAfter(new Date(), parseISO(task.next_run)))
      .sort((a, b) => {
        const dateA = parseISO(a.next_run);
        const dateB = parseISO(b.next_run);
        return dateA.getTime() - dateB.getTime();
      });
  }, [tasks]);

  const shortenTaskName = (name) => {
    if (name.includes('Cloud Sync: Microsoft OneDrive') && name.includes('/Immich')) {
      return 'Cloud Sync: Immich -> OneDrive';
    }
    return name;
  };

  const getServiceIcon = (service) => {
    switch (service) {
      case 'immich': return '/immich.png';
      case 'truenas': return '/truenas_logo.png';
      case 'rpi': return '/raspberry-pi.png';
      default: return null;
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'rgba(255, 255, 255, 0.5)';
    const s = status.toLowerCase();
    if (s === 'running' || s === 'active') return '#00d4ff';
    if (s === 'idle' || s === 'success' || s === 'enabled' || s === 'completed') return '#00ff88';
    if (s === 'failed' || s === 'error' || s === 'disabled') return '#ff4444';
    return '#ffeb3b';
  };

  const formatSafeDate = (dateStr, formatStr = 'PPp') => {
    if (!dateStr) return 'N/A';
    try {
      const date = parseISO(dateStr);
      if (!isValid(date)) return 'Invalid Date';
      return format(date, formatStr);
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const formatTaskDate = (dateStr) => {
    try {
      const d = parseISO(dateStr);
      if (!isValid(d)) return 'Unknown';
      if (isToday(d)) return 'Today';
      if (isTomorrow(d)) return 'Tomorrow';
      return format(d, 'MMM d');
    } catch (e) {
      return 'Unknown';
    }
  };

  const handleShutdownClick = () => {
    setOpenShutdownDialog(true);
  };

  const handleShutdownConfirm = () => {
    setOpenShutdownDialog(false);
    if (onShutdown) {
      onShutdown();
    }
  };

  const handleShutdownCancel = () => {
    setOpenShutdownDialog(false);
  };

  // Helper function to format dates in Warsaw timezone (UTC+1 in winter)
  const formatDateInWarsaw = (dateString, options = {}) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      // Warsaw is UTC+1 in winter (CET), add 1 hour
      const warsawDate = new Date(date.getTime() + (1 * 60 * 60 * 1000));
      // Format as local time (which will now be Warsaw time)
      return warsawDate.toLocaleString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(',', '');
    } catch (error) {
      console.error('Error formatting date in Warsaw timezone:', error);
      return dateString;
    }
  };

  // Helper function to adjust dates for Warsaw timezone (UTC+1 in winter)
  const adjustForWarsawTimezone = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      // Warsaw is UTC+1 in winter (CET), add 1 hour
      return new Date(date.getTime() + (1 * 60 * 60 * 1000));
    } catch (error) {
      console.error('Error adjusting date for Warsaw timezone:', error);
      return new Date(dateString);
    }
  };

  const formatUptime = (seconds) => {
    if (!seconds) return '0m';
    const d = Math.floor(seconds / (3600 * 24));
    const h = Math.floor((seconds % (3600 * 24)) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
      return `${m}m`;
  };

  const unifiWan1 = React.useMemo(() => {
    if (!unifiOverview || !Array.isArray(unifiOverview.wan)) return null;
    return unifiOverview.wan.find(w => w.name === 'WAN 1') || unifiOverview.wan[0] || null;
  }, [unifiOverview]);

  const unifiWan2 = React.useMemo(() => {
    if (!unifiOverview || !Array.isArray(unifiOverview.wan)) return null;
    return unifiOverview.wan.find(w => w.name === 'WAN 2') || unifiOverview.wan[1] || null;
  }, [unifiOverview]);

  const unifiFailoverActive = Boolean(unifiOverview?.failover_active);
  const unifiWanDisplay = React.useMemo(() => {
    if (unifiFailoverActive && unifiWan2) return unifiWan2;
    return unifiWan1;
  }, [unifiFailoverActive, unifiWan1, unifiWan2]);

  const unifiTrueNasPort = React.useMemo(() => {
    if (!unifiPorts || !Array.isArray(unifiPorts.devices)) return null;
    const device = unifiPorts.devices.find(
      (d) => d.type === 'usw' && d.name === 'USW Flex 2.5G 5'
    );
    const port = device?.ports?.find((p) => p.port_index === 4);
    if (!device || !port) return null;
    return {
      deviceName: device.name,
      ...port,
    };
  }, [unifiPorts]);

  const unifiGatewayStats = React.useMemo(() => {
    if (!unifiOverview || !unifiOverview.gateway) return null;
    const gw = unifiOverview.gateway;

    const rawCpu = gw.cpu_percent;
    const cpuNum = rawCpu == null ? null : Number(rawCpu);
    const cpuPercent = cpuNum != null && !Number.isNaN(cpuNum) ? cpuNum : null;

    const rawRam = gw.ram_percent;
    const ramNum = rawRam == null ? null : Number(rawRam);
    const ramPercent = ramNum != null && !Number.isNaN(ramNum) ? ramNum : null;

    const rawTemp = gw.cpu_temp;
    const tempNum = rawTemp == null ? null : Number(rawTemp);
    const cpuTemp = tempNum != null && !Number.isNaN(tempNum) ? tempNum : null;

    const rawUptime = gw.uptime_hours;
    const uptimeNum = rawUptime == null ? null : Number(rawUptime);
    const uptimeHours = uptimeNum != null && !Number.isNaN(uptimeNum) ? uptimeNum : null;

    return {
      cpu: cpuPercent,
      cpuTemp,
      ram: ramPercent,
      freeRam: ramPercent != null ? 100 - ramPercent : null,
      uptimeHours,
      name: gw.name,
    };
  }, [unifiOverview]);

  if (loading && !status) {
    return (
      <StyledCard sx={{ p: 4, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
          <Router sx={{ fontSize: 48, color: 'rgba(0, 212, 255, 0.5)', mr: 2 }} />
          <Box>
            <Typography variant="h4" sx={{
              background: 'linear-gradient(135deg, #00d4ff, #ffffff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 900,
              letterSpacing: '2px',
              mb: 1
            }}>
              WatchTower
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Connecting to TrueNAS Server...
            </Typography>
          </Box>
        </Box>
        <LinearProgress
          sx={{
            height: 4,
            borderRadius: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #00d4ff, #ff4081)',
              borderRadius: 2,
            },
          }}
        />
      </StyledCard>
    );
  }

  // Handle case where status is null or API failed
  if (!status || !status.truenas) {
    return (
      <StyledCard sx={{ p: 4, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
          <Router sx={{ fontSize: 48, color: 'rgba(255, 68, 68, 0.5)', mr: 2 }} />
          <Box>
            <Typography variant="h4" sx={{
              background: 'linear-gradient(135deg, #ff4444, #ff6b6b)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 900,
              letterSpacing: '2px',
              mb: 1
            }}>
              WatchTower
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Unable to connect to backend API
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 1 }}>
              Please check if the backend server is running
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          onClick={() => window.location.reload()}
          sx={{
            mt: 2,
            borderColor: 'rgba(255, 68, 68, 0.5)',
            color: '#ff4444',
            '&:hover': {
              borderColor: '#ff4444',
              backgroundColor: 'rgba(255, 68, 68, 0.1)',
            },
          }}
        >
          Retry Connection
        </Button>
      </StyledCard>
    );
  }

  const { truenas, apps } = status;

  const DISK_MAP = {
    'sda': { type: 'hdd', icon: '/hdd.png' },
    'sdb': { type: 'hdd', icon: '/hdd.png' },
    'sdc': { type: 'ssd', icon: '/ssd.png' },
    'nvme0n1': { type: 'nvme', icon: '/nvme.png' },
    'nvme1n1': { type: 'nvme', icon: '/nvme.png' },
  };

  const getDiskInfo = (name) => DISK_MAP[name] || { type: 'unknown', icon: '/hdd.png' };

  // Stable disk-to-pool assignment by model (device names like nvme0n1 can swap after reboot)
  const POOL_DISK_RULES = {
    'system': {
      modelContains: ['Lexar', 'NM620'],
      sizeGbMin: 200,
      sizeGbMax: 300,
      count: 1,
      type: 'system',
    },
    'stripe nvme': {
      modelContains: ['ADATA', 'LEGEND'],
      count: 1,
      type: 'normal',
    },
    'raidz1 hdd': {
      modelContains: ['ST4000VN006'],
      count: 2,
      type: 'protected',
    },
    'stripe ssd': {
      modelContains: ['WDC', 'WDS100T1R0A', '68A4W0'],
      count: 1,
      type: 'normal',
    },
  };

  const FIXED_POOL_DISPLAY_ORDER = ['raidz1 hdd', 'stripe ssd', 'stripe nvme', 'system'];

  function resolveDrivesByModel(disks, rule) {
    if (!Array.isArray(disks) || !rule || !rule.modelContains) return [];
    const modelLower = (s) => (s || '').toLowerCase();
    const matched = disks.filter((d) => {
      const m = modelLower(d.model);
      const ok = rule.modelContains.some((sub) => m.includes(modelLower(sub)));
      if (!ok) return false;
      const gb = d.size_gb;
      if (rule.sizeGbMin != null && (gb == null || gb < rule.sizeGbMin)) return false;
      if (rule.sizeGbMax != null && (gb == null || gb > rule.sizeGbMax)) return false;
      return true;
    });
    const names = matched.map((d) => d.name).filter(Boolean);
    const count = rule.count != null ? rule.count : names.length;
    const sorted = [...names].sort();
    return sorted.slice(0, count);
  }

  const allDisks = truenas.disks || [];

  // Organize disks into pools: API topology when present, else resolve by model for stable assignment
  const poolData = [
    ...(truenas.pools || []).map((pool) => {
      const normalizedName = (pool.name || '').toLowerCase().replace(/_/g, ' ').trim();
      const rule = POOL_DISK_RULES[normalizedName];
      let drives = Array.isArray(pool.drives) && pool.drives.length > 0 ? [...pool.drives].sort() : [];
      if (drives.length === 0 && rule) {
        drives = resolveDrivesByModel(allDisks, rule);
      }
      const type = (rule && rule.type) || (normalizedName.includes('raidz1') ? 'protected' : 'normal');
      return { ...pool, drives, type };
    }),
    // System pool: boot drive identified by model (Lexar NM620 256GB), not by device name
    (() => {
      const systemRule = POOL_DISK_RULES['system'];
      const systemDrives = resolveDrivesByModel(allDisks, systemRule);
      return {
        name: 'System',
        health: 'ONLINE',
        type: 'system',
        size: 0,
        allocated: 0,
        allocated_human: 'N/A',
        free_human: 'N/A',
        drives: systemDrives.length > 0 ? systemDrives : [],
      };
    })(),
  ].sort((a, b) => {
    const norm = (p) => (p.name || '').toLowerCase().replace(/_/g, ' ').trim();
    const orderA = FIXED_POOL_DISPLAY_ORDER.indexOf(norm(a));
    const orderB = FIXED_POOL_DISPLAY_ORDER.indexOf(norm(b));
    if (orderA !== -1 && orderB !== -1) return orderA - orderB;
    if (orderA !== -1) return -1;
    if (orderB !== -1) return 1;
    if (a.type !== b.type) {
      if (a.type === 'protected') return -1;
      if (b.type === 'protected') return 1;
      if (a.type === 'system') return 1;
      if (b.type === 'system') return -1;
    }
    return (a.name || '').localeCompare(b.name || '');
  });

  const renderLastSeenChip = () => (
    <ModernChip
      label={`Last seen: ${formatDistanceToNow(adjustForWarsawTimezone(truenas.last_seen), { addSuffix: true })}`}
      colorType="info"
      icon={<AccessTime sx={{ fontSize: 18 }} />}
    />
  );

  return (
    <StyledCard>
      <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        {/* Header: logo + title; on mobile compact stack + scrollable metrics + wrapped actions */}
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            mb: { xs: 2, sm: 4 },
            [theme.breakpoints.down('sm')]: {
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 1.5,
            },
          })}
        >
          {/* Logo + title — compact on mobile */}
          <Box
            display="flex"
            alignItems="center"
            sx={(theme) => ({
              flexShrink: 0,
              [theme.breakpoints.down('sm')]: {
                width: '100%',
                pb: 0.5,
                borderBottom: '1px solid rgba(255,255,255,0.06)',
              },
            })}
          >
            <Box
              sx={(theme) => ({
                position: 'relative',
                mr: 2,
                [theme.breakpoints.down('sm')]: { mr: 1.5 },
              })}
            >
              <Link
                href="https://truenas.raidnet.pl/"
                target="_blank"
                rel="noreferrer"
                sx={(theme) => ({
                  display: 'inline-flex',
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  overflow: 'hidden',
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'translateY(-2px)' },
                  [theme.breakpoints.down('sm')]: {
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                  },
                })}
              >
                <Box
                  component="img"
                  src="/truenas_logo.png"
                  alt="TrueNAS"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: truenas.is_online ? 'none' : 'grayscale(0.6)',
                    borderRadius: 2,
                  }}
                />
              </Link>
              <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
                <StatusIndicator isOnline={truenas.is_online} />
              </Box>
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Link
                href="https://truenas.raidnet.pl/"
                target="_blank"
                rel="noreferrer"
                sx={{ textDecoration: 'none' }}
                aria-label="Visit TrueNAS server"
              >
                <Typography
                  variant="h4"
                  sx={{
                    background: 'linear-gradient(135deg, #ffffff, #e0e0e0)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 700,
                    mb: 0.5,
                    fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' },
                    lineHeight: 1.2,
                  }}
                >
                  TrueNAS Server
                </Typography>
              </Link>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: { xs: '0.8rem', sm: '1.1rem' } }}>
                  {truenas.ip_address}
                </Typography>
                {truenas.version && (
                  <Chip
                    label={truenas.version.split('-')[0]}
                    size="small"
                    sx={(theme) => ({
                      height: 20,
                      fontSize: '0.65rem',
                      bgcolor: 'rgba(255,255,255,0.05)',
                      color: 'rgba(255,255,255,0.5)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      [theme.breakpoints.down('sm')]: { height: 18, fontSize: '0.6rem' },
                    })}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Mobile only: 2×3 badge grid, equal row height, no Live toggle */}
          <Box
            sx={(theme) => ({
              display: 'none',
              [theme.breakpoints.down('sm')]: {
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gridTemplateRows: '1fr 1fr',
                gap: 1,
                alignItems: 'stretch',
                width: '100%',
                '& > *': { minHeight: 48 },
              },
            })}
          >
            {truenas.is_online ? (
              <StatPill color="0, 212, 255" sx={{ height: '100%' }}>
                <History sx={{ color: '#00d4ff', fontSize: 18 }} />
                <Box>
                  <Typography variant="caption" display="block" sx={{ color: 'rgba(0, 212, 255, 0.7)', lineHeight: 1, fontSize: '0.7rem' }}>UPTIME</Typography>
                  <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600, lineHeight: 1, fontSize: '0.8rem' }}>{formatUptime(truenas.uptime)}</Typography>
                </Box>
              </StatPill>
            ) : (
              <Box sx={{ minHeight: 48 }} />
            )}
            {truenas.is_online ? (
              <StatPill color="0, 255, 136" sx={{ height: '100%' }}>
                <Assessment sx={{ color: '#00ff88', fontSize: 18 }} />
                <Box sx={{ textAlign: 'center', minWidth: 50 }}>
                  <Typography variant="caption" display="block" sx={{ color: 'rgba(0, 255, 136, 0.7)', lineHeight: 1, fontSize: '0.7rem' }}>LOAD</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, lineHeight: 1, fontSize: '1rem', textAlign: 'center' }}>
                    {truenas.loadavg ? truenas.loadavg[0].toFixed(2) : '0.00'}
                  </Typography>
                </Box>
              </StatPill>
            ) : (
              <Box sx={{ minHeight: 48 }} />
            )}
            {(truenas.is_online && (powerData?.current_power || powerData?.yearly_usage)) ? (
              <StatPill color="255, 235, 59" onClick={handleHistoryClick} sx={{ cursor: 'pointer', height: '100%' }}>
                <Bolt sx={{ color: '#ffeb3b', fontSize: 18 }} />
                <Box>
                  <Typography variant="caption" display="block" sx={{ color: 'rgba(255, 235, 59, 0.7)', lineHeight: 1, fontSize: '0.7rem' }}>POWER</Typography>
                  <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600, lineHeight: 1, fontSize: '0.8rem' }}>
                    {powerData?.current_power ? `${parseFloat(powerData.current_power.state).toFixed(0)} W` : 'N/A'}
                  </Typography>
                </Box>
              </StatPill>
            ) : (
              <Box sx={{ minHeight: 48 }} />
            )}
            <Box sx={{ display: 'flex', alignItems: 'stretch', height: '100%' }}>
              <ModernChip
                label={truenas.is_online ? 'Online' : 'Offline'}
                isOnline={truenas.is_online}
                colorType={truenas.is_online ? 'success' : 'error'}
                icon={truenas.is_online ? <Wifi /> : <WifiOff />}
                sx={{ width: '100%', height: '100%', justifyContent: 'center' }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'stretch', height: '100%' }}>
              {truenas.response_time != null ? (
                <ModernChip
                  label={`${truenas.response_time.toFixed(0)}ms`}
                  colorType="success"
                  icon={<NetworkCheck sx={{ fontSize: 18 }} />}
                  sx={{ width: '100%', height: '100%', justifyContent: 'center' }}
                />
              ) : (
                <Box sx={{ width: '100%', minHeight: 48 }} />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'stretch', height: '100%' }}>
              <ShutdownButton
                startIcon={<PowerOff />}
                onClick={handleShutdownClick}
                disabled={shutdownLoading || !truenas.is_online}
                sx={{ flex: 1, height: '100%', justifyContent: 'center' }}
              >
                {shutdownLoading ? '…' : 'Off'}
              </ShutdownButton>
            </Box>
          </Box>

          {/* Desktop: single row — all badges (Uptime, Load, Power, VMs, Online, Latency, Off) — fixed width for consistent size */}
          <Box
            sx={(theme) => ({
              display: 'none',
              [theme.breakpoints.up('sm')]: {
                display: 'flex',
                flex: 1,
                minWidth: 0,
                alignItems: 'center',
                justifyContent: 'flex-end',
                flexWrap: 'wrap',
                gap: 1.5,
                ml: 3,
              },
              [theme.breakpoints.up('md')]: { ml: 5 },
            })}
          >
            {truenas.is_online && (
              <>
                <StatPill color="0, 212, 255" sx={{ width: 110, minWidth: 110, height: 48, alignItems: 'center' }}>
                  <History sx={{ color: '#00d4ff', fontSize: 18 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" display="block" sx={{ color: 'rgba(0, 212, 255, 0.7)', lineHeight: 1, fontSize: '0.65rem' }}>UPTIME</Typography>
                    <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600, lineHeight: 1 }} noWrap>{formatUptime(truenas.uptime)}</Typography>
                  </Box>
                </StatPill>
                <StatPill color="0, 255, 136" sx={{ width: 110, minWidth: 110, height: 48, alignItems: 'center' }}>
                  <Assessment sx={{ color: '#00ff88', fontSize: 18 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" display="block" sx={{ color: 'rgba(0, 255, 136, 0.7)', lineHeight: 1, fontSize: '0.65rem' }}>LOAD</Typography>
                    <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600, lineHeight: 1 }}>
                      {truenas.loadavg ? truenas.loadavg[0].toFixed(2) : '0.00'}
                    </Typography>
                  </Box>
                </StatPill>
                {(powerData?.current_power || powerData?.yearly_usage) && (
                  <StatPill color="255, 235, 59" onClick={handleHistoryClick} sx={{ cursor: 'pointer', width: 110, minWidth: 110, height: 48, alignItems: 'center' }}>
                    <Bolt sx={{ color: '#ffeb3b', fontSize: 18 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" display="block" sx={{ color: 'rgba(255, 235, 59, 0.7)', lineHeight: 1, fontSize: '0.65rem' }}>POWER</Typography>
                      <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600, lineHeight: 1 }}>
                        {powerData.current_power ? `${parseFloat(powerData.current_power.state).toFixed(0)} W` : 'N/A'}
                      </Typography>
                    </Box>
                  </StatPill>
                )}
              </>
            )}
            {/* Running VM Icons */}
            {status.virtual_machines && status.virtual_machines.filter(vm => {
              const rawStatus = vm.status || vm.state || '';
              const vmStatus = (typeof rawStatus === 'object' && rawStatus !== null) 
                ? (rawStatus.state || rawStatus.name || String(rawStatus)) 
                : rawStatus;
              const normalizedStatus = String(vmStatus).toLowerCase();
              return normalizedStatus === 'running' || normalizedStatus === 'up';
            }).map(vm => {
              const vmKey = vm.id || vm.name;
              const iconId = vmIcons[vmKey];
              const vmIcon = iconId ? VM_ICONS.find(icon => icon.id === iconId) : null;
              const vmName = vm.name || 'Unknown VM';
              const vmVCPU = vm.vcpus || vm.cpu || vm.cores || '-';
              const vmMemory = vm.memory || vm.ram || vm.memory_in_mb || '-';
              
              return (
                <Tooltip
                  key={vmKey}
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {vmName}
                      </Typography>
                      <Typography variant="caption" display="block">
                        vCPU: {vmVCPU}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Memory: {vmMemory} MB
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ color: '#00ff88', mt: 0.5 }}>
                        Status: Running
                      </Typography>
                    </Box>
                  }
                  arrow
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      background: 'rgba(0, 255, 136, 0.1)',
                      border: '1px solid rgba(0, 255, 136, 0.3)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {vmIcon ? (
                      <Box
                        component="img"
                        src={vmIcon.path}
                        alt={vmIcon.name}
                        sx={{ width: 22, height: 22, objectFit: 'contain' }}
                      />
                    ) : (
                      <ComputerIcon sx={{ fontSize: 20, color: '#00ff88' }} />
                    )}
                  </span>
                </Tooltip>
              );
            })}
            
            <ModernChip
              label={truenas.is_online ? "Online" : "Offline"}
              isOnline={truenas.is_online}
              colorType={truenas.is_online ? "success" : "error"}
              icon={truenas.is_online ? <Wifi /> : <WifiOff />}
              sx={{ minWidth: 100, height: 40, '& .MuiChip-label': { overflow: 'visible', whiteSpace: 'nowrap' } }}
            />
            {truenas.response_time != null && (
              <ModernChip
                label={`${truenas.response_time.toFixed(0)}ms`}
                colorType="success"
                icon={<NetworkCheck sx={{ fontSize: 18 }} />}
                sx={{ minWidth: 100, height: 40, '& .MuiChip-label': { overflow: 'visible', whiteSpace: 'nowrap' } }}
              />
            )}

            <ShutdownButton
              startIcon={<PowerOff />}
              onClick={handleShutdownClick}
              disabled={shutdownLoading || !truenas.is_online}
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            >
              {shutdownLoading ? 'Shutting down...' : 'Turn Off'}
            </ShutdownButton>
            <ShutdownButton
              startIcon={<PowerOff />}
              onClick={handleShutdownClick}
              disabled={shutdownLoading || !truenas.is_online}
              sx={{ display: { xs: 'inline-flex', sm: 'none' }, flexShrink: 0 }}
            >
              {shutdownLoading ? '…' : 'Off'}
            </ShutdownButton>
          </Box>


        </Box>

        {/* Utilization Section */}
        <MobileScrollRow>
          {isMobile ? (
            <>
              {/* Combined CPU + RAM (mobile) — new design */}
              <UtilizationCard sx={{ minHeight: { xs: 220 } }}>
                {/* CPU Section */}
                <Box sx={{ mb: 2 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Box
                      component="img"
                      src="/intel.png"
                      alt="CPU"
                      sx={{ width: 22, height: 22, objectFit: 'contain', filter: 'drop-shadow(0 0 4px rgba(0, 212, 255, 0.5))' }}
                    />
                    <Typography variant="subtitle2" sx={{ color: '#ffffff', fontWeight: 700, flex: 1, fontSize: '0.95rem' }}>
                      CPU
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: '#00d4ff', fontWeight: 800, fontSize: '1rem' }}>
                      {cpuRamData?.cpu?.usage?.toFixed(1) ?? (truenas.cpu_usage ? truenas.cpu_usage.toFixed(1) : '0')}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={cpuRamData?.cpu?.usage ?? truenas.cpu_usage ?? 0}
                    sx={{
                      height: 8, borderRadius: 4,
                      backgroundColor: 'rgba(0, 212, 255, 0.08)',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(90deg, #00d4ff, #0099ff)',
                        borderRadius: 4,
                        boxShadow: '0 0 8px rgba(0, 212, 255, 0.4)',
                      },
                    }}
                  />
                  <Box sx={{ mt: 0.75, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem' }}>
                      {cpuRamData?.cpu?.model || 'i5-13400'} ({cpuRamData?.cpu?.cores || 16}C)
                    </Typography>
                    {(cpuRamData?.cpu?.temperature || truenas.cpu_temperature) > 0 && (
                      <Typography variant="caption" sx={{ color: '#ffeb3b', fontWeight: 600, fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <DeviceThermostat sx={{ fontSize: 14 }} />
                        {(cpuRamData?.cpu?.temperature ?? truenas.cpu_temperature)?.toFixed(1)}°C
                      </Typography>
                    )}
                  </Box>
                  {/* Load Average */}
                  <Box sx={{ 
                    mt: 1.5, 
                    p: 1, 
                    bgcolor: 'rgba(255,255,255,0.02)', 
                    borderRadius: '8px', 
                    border: '1px solid rgba(255,255,255,0.04)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.5 }}>
                      LOAD AVERAGE
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                      {['1m', '5m', '15m'].map((label, i) => {
                        const val = (cpuRamData?.cpu?.loadavg || truenas.loadavg || [0,0,0])[i];
                        return (
                          <Box key={label} sx={{ flex: 1, textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: '1.1rem' }}>
                              {val?.toFixed(2) ?? '0.00'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.55rem' }}>
                              {label}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>

                {/* RAM Section */}
                <Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Box
                      component="img"
                      src="/RAM.png"
                      alt="RAM"
                      sx={{ width: 22, height: 22, objectFit: 'contain', filter: 'drop-shadow(0 0 4px rgba(0, 255, 136, 0.5))' }}
                    />
                    <Typography variant="subtitle2" sx={{ color: '#ffffff', fontWeight: 700, flex: 1, fontSize: '0.95rem' }}>
                      RAM
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                      {cpuRamData?.ram?.total_gb ?? (truenas.memory_total ? (truenas.memory_total / (1024**3)).toFixed(1) : '0')} GiB
                    </Typography>
                  </Box>
                  {/* Total Used bar */}
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>TOTAL USED</Typography>
                      <Typography variant="caption" sx={{ color: '#ff6b6b', fontWeight: 700, fontSize: '0.75rem' }}>
                        {cpuRamData?.ram?.used_gb?.toFixed(1) ?? '0'} GiB ({cpuRamData?.ram?.used_pct?.toFixed(1) ?? '0'}%)
                      </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={cpuRamData?.ram?.used_pct ?? 0}
                      sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(255,107,107,0.08)',
                        '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #ff6b6b, #ff4444)', borderRadius: 3 } }} />
                  </Box>
                  {/* Apps Only bar */}
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>APPS ONLY</Typography>
                      <Typography variant="caption" sx={{ color: '#00ff88', fontWeight: 700, fontSize: '0.75rem' }}>
                        {cpuRamData?.ram?.apps_gb?.toFixed(1) ?? '0'} GiB ({cpuRamData?.ram?.apps_pct?.toFixed(1) ?? '0'}%)
                      </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={cpuRamData?.ram?.apps_pct ?? 0}
                      sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(0,255,136,0.08)',
                        '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #00ff88, #00d4aa)', borderRadius: 3 } }} />
                  </Box>
                  {/* ZFS ARC bar */}
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <Bolt sx={{ fontSize: 12, color: '#ffd700' }} /> ZFS CACHE
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#ffd700', fontWeight: 700, fontSize: '0.75rem' }}>
                        {cpuRamData?.ram?.arc_gb?.toFixed(1) ?? '0'} GiB ({cpuRamData?.ram?.arc_pct?.toFixed(1) ?? '0'}%)
                      </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={cpuRamData?.ram?.arc_pct ?? 0}
                      sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(255,215,0,0.08)',
                        '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #ffd700, #ff8c00)', borderRadius: 3, boxShadow: '0 0 6px rgba(255,215,0,0.3)' } }} />
                  </Box>
                </Box>
              </UtilizationCard>
            </>
          ) : (
            <>
              {/* CPU Utilization (desktop/tablet) — redesigned */}
              <UtilizationCard>
                {/* Header with icon and usage */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Box
                      component="img"
                      src="/intel.png"
                      alt="Intel CPU"
                      sx={{
                        width: 36, height: 36, objectFit: 'contain',
                        filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.6))',
                      }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, lineHeight: 1.2 }}>
                        CPU
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>
                        {cpuRamData?.cpu?.model || 'i5-13400'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="h4" sx={{ color: '#00d4ff', fontWeight: 800, lineHeight: 1, fontSize: '1.8rem' }}>
                      {cpuRamData?.cpu?.usage?.toFixed(1) ?? (truenas.cpu_usage ? truenas.cpu_usage.toFixed(1) : '0')}%
                    </Typography>
                  </Box>
                </Box>

                {/* Usage bar */}
                <LinearProgress
                  variant="determinate"
                  value={cpuRamData?.cpu?.usage ?? truenas.cpu_usage ?? 0}
                  sx={{
                    height: 8, borderRadius: 4,
                    backgroundColor: 'rgba(0, 212, 255, 0.08)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #00d4ff, #0099ff, #00d4ff)',
                      borderRadius: 4,
                      boxShadow: '0 0 12px rgba(0, 212, 255, 0.4)',
                    },
                  }}
                />

                {/* Temperature + Cores row */}
                <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      size="small"
                      label={`${cpuRamData?.cpu?.cores || 16} Cores`}
                      sx={{
                        height: 22, fontSize: '0.65rem', fontWeight: 700,
                        bgcolor: 'rgba(0, 212, 255, 0.1)', color: '#00d4ff',
                        border: '1px solid rgba(0, 212, 255, 0.2)',
                      }}
                    />
                  </Box>
                  {(cpuRamData?.cpu?.temperature || truenas.cpu_temperature) > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DeviceThermostat sx={{ fontSize: 16, color: '#ffeb3b' }} />
                      <Typography variant="body2" sx={{ color: '#ffeb3b', fontWeight: 700, fontSize: '0.85rem' }}>
                        {(cpuRamData?.cpu?.temperature ?? truenas.cpu_temperature)?.toFixed(1)}°C
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Load Average */}
                <Box sx={{ 
                  mt: 1.5, 
                  p: 1, 
                  bgcolor: 'rgba(255,255,255,0.02)', 
                  borderRadius: '8px', 
                  border: '1px solid rgba(255,255,255,0.04)',
                  [theme.breakpoints.down('sm')]: {
                    textAlign: 'center',
                    '& .load-avg-title': {
                      textAlign: 'center',
                      fontSize: '0.7rem',
                      color: 'rgba(255,255,255,0.5)',
                    },
                    '& .load-avg-value': {
                      fontSize: '1.1rem',
                      color: 'rgba(255,255,255,0.9)',
                    },
                  }
                }}>
                  <Typography variant="caption" className="load-avg-title" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.6rem', fontWeight: 600, display: 'block', mb: 0.5 }}>
                    LOAD AVERAGE
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    {['1m', '5m', '15m'].map((label, i) => {
                      const val = (cpuRamData?.cpu?.loadavg || truenas.loadavg || [0,0,0])[i];
                      return (
                        <Box key={label} sx={{ flex: 1, textAlign: 'center' }}>
                          <Typography variant="body2" className="load-avg-value" sx={{ color: '#ffffff', fontWeight: 700, fontSize: '0.85rem' }}>
                            {val?.toFixed(2) ?? '0.00'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.55rem' }}>
                            {label}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {/* Per-core temperatures (collapsed, show first 10) */}
                {cpuRamData?.cpu?.temp_map && Object.keys(cpuRamData.cpu.temp_map).filter(k => k.startsWith('cpu') && k !== 'cpu').length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.6rem', fontWeight: 600, display: 'block', mb: 0.5 }}>
                      CORE TEMPERATURES
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {Object.keys(cpuRamData.cpu.temp_map)
                        .filter(k => k.startsWith('cpu') && k !== 'cpu')
                        .sort((a, b) => parseInt(a.replace('cpu', '')) - parseInt(b.replace('cpu', '')))
                        .map(core => {
                          const temp = cpuRamData.cpu.temp_map[core];
                          const tempColor = temp > 80 ? '#ff4444' : temp > 65 ? '#ffeb3b' : '#00ff88';
                          return (
                            <Chip
                              key={core}
                              size="small"
                              label={`C${core.replace('cpu', '')}: ${temp}°`}
                              sx={{
                                height: 18, fontSize: '0.55rem', fontWeight: 600,
                                bgcolor: `${tempColor}15`, color: tempColor,
                                border: `1px solid ${tempColor}30`,
                                '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          );
                        })}
                    </Box>
                  </Box>
                )}
              </UtilizationCard>

              {/* RAM Utilization (desktop/tablet) — redesigned with 3 progress bars and Pie Chart */}
              <UtilizationCard>
                {/* Header with icon */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Box
                      component="img"
                      src="/RAM.png"
                      alt="RAM"
                      sx={{
                        width: 36, height: 36, objectFit: 'contain',
                        filter: 'drop-shadow(0 0 8px rgba(0, 255, 136, 0.5))',
                      }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, lineHeight: 1.2 }}>
                        Memory
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>
                        {cpuRamData?.ram?.total_gb ?? (truenas.memory_total ? (truenas.memory_total / (1024**3)).toFixed(1) : '0')} GiB Total
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="h4" sx={{ color: '#00ff88', fontWeight: 800, lineHeight: 1, fontSize: '1.8rem' }}>
                    {cpuRamData?.ram?.used_pct?.toFixed(1) ?? (truenas.memory_usage ? truenas.memory_usage.toFixed(1) : '0')}%
                  </Typography>
                </Box>

                <Grid container spacing={2} alignItems="center" sx={{ flexGrow: 1 }}>
                  <Grid item xs={12} md={7} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    {/* Total Used bar */}
                    <Box sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ff6b6b' }} />
                          TOTAL USED
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#ff6b6b', fontWeight: 700, fontSize: '0.85rem' }}>
                          {cpuRamData?.ram?.used_gb?.toFixed(2) ?? '0'} GiB
                          <Typography component="span" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.7rem', ml: 0.5 }}>
                            ({cpuRamData?.ram?.used_pct?.toFixed(1) ?? '0'}%)
                          </Typography>
                        </Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={cpuRamData?.ram?.used_pct ?? 0}
                        sx={{
                          height: 8, borderRadius: 4, bgcolor: 'rgba(255,107,107,0.08)',
                          '& .MuiLinearProgress-bar': {
                            background: 'linear-gradient(90deg, #ff6b6b, #ff4444)',
                            borderRadius: 4, boxShadow: '0 0 10px rgba(255,68,68,0.3)',
                          },
                        }} />
                    </Box>

                    {/* Apps Only bar */}
                    <Box sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#00ff88' }} />
                          APPS ONLY
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#00ff88', fontWeight: 700, fontSize: '0.85rem' }}>
                          {cpuRamData?.ram?.apps_gb?.toFixed(2) ?? '0'} GiB
                          <Typography component="span" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.7rem', ml: 0.5 }}>
                            ({cpuRamData?.ram?.apps_pct?.toFixed(1) ?? '0'}%)
                          </Typography>
                        </Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={cpuRamData?.ram?.apps_pct ?? 0}
                        sx={{
                          height: 8, borderRadius: 4, bgcolor: 'rgba(0,255,136,0.08)',
                          '& .MuiLinearProgress-bar': {
                            background: 'linear-gradient(90deg, #00ff88, #00d4aa)',
                            borderRadius: 4, boxShadow: '0 0 10px rgba(0,255,136,0.3)',
                          },
                        }} />
                    </Box>

                    {/* ZFS ARC Cache bar */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Bolt sx={{ fontSize: 12, color: '#ffd700' }} />
                          ZFS ARC CACHE
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#ffd700', fontWeight: 700, fontSize: '0.85rem' }}>
                          {cpuRamData?.ram?.arc_gb?.toFixed(2) ?? '0'} GiB
                          <Typography component="span" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.7rem', ml: 0.5 }}>
                            ({cpuRamData?.ram?.arc_pct?.toFixed(1) ?? '0'}%)
                          </Typography>
                        </Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={cpuRamData?.ram?.arc_pct ?? 0}
                        sx={{
                          height: 8, borderRadius: 4, bgcolor: 'rgba(255,215,0,0.08)',
                          '& .MuiLinearProgress-bar': {
                            background: 'linear-gradient(90deg, #ffd700, #ff8c00)',
                            borderRadius: 4, boxShadow: '0 0 10px rgba(255,215,0,0.3)',
                          },
                        }} />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ width: '100%', height: 180, position: 'relative' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Apps Only', value: cpuRamData?.ram?.apps_pct ?? 0, color: '#00ff88', unit: 'GiB', val: cpuRamData?.ram?.apps_gb?.toFixed(2) },
                              { name: 'ZFS ARC', value: cpuRamData?.ram?.arc_pct ?? 0, color: '#ffd700', unit: 'GiB', val: cpuRamData?.ram?.arc_gb?.toFixed(2) },
                              { name: 'Free / Overhead', value: Math.max(0, 100 - (cpuRamData?.ram?.apps_pct ?? 0) - (cpuRamData?.ram?.arc_pct ?? 0)), color: 'rgba(255,255,255,0.05)', unit: '', val: '' }
                            ]}
                            cx="50%"
                            cy="50%"
                            innerRadius={45}
                            outerRadius={65}
                            paddingAngle={2}
                            dataKey="value"
                            stroke="none"
                            startAngle={90}
                            endAngle={-270}
                          >
                            {[
                              { color: '#00ff88' },
                              { color: '#ffd700' },
                              { color: 'rgba(255,255,255,0.05)' }
                            ].map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} style={{ filter: entry.color !== 'rgba(255,255,255,0.05)' ? `drop-shadow(0 0 8px ${entry.color}80)` : 'none', outline: 'none' }} />
                            ))}
                          </Pie>
                          <RechartsTooltip
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <Box sx={{
                                    bgcolor: 'rgba(0,0,0,0.85)',
                                    backdropFilter: 'blur(10px)',
                                    border: `1px solid ${data.color === 'rgba(255,255,255,0.05)' ? 'rgba(255,255,255,0.2)' : data.color}`,
                                    p: 1,
                                    borderRadius: '8px',
                                    boxShadow: data.color === 'rgba(255,255,255,0.05)' ? 'none' : `0 0 15px ${data.color}40`
                                  }}>
                                    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700, display: 'block' }}>
                                      {data.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: data.color === 'rgba(255,255,255,0.05)' ? '#fff' : data.color, fontWeight: 800 }}>
                                      {data.value.toFixed(1)}% {data.val ? `(${data.val} ${data.unit})` : ''}
                                    </Typography>
                                  </Box>
                                );
                              }
                              return null;
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </Grid>
                </Grid>
              </UtilizationCard>
            </>
          )}

          {/* Network Utilization */}
          <UtilizationCard failover={unifiFailoverActive}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
                mb: 1,
              }}
            >
              <Box display="flex" alignItems="center">
                <Box
                  component="img"
                  src="/unifi.png"
                  alt="UniFi"
                  sx={{
                    width: 28,
                    height: 28,
                    mr: 1.5,
                    borderRadius: '50%',
                    boxShadow: '0 0 12px rgba(0, 212, 255, 0.7)',
                  }}
                />
                <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
                  Network
                </Typography>
              </Box>

              {unifiGatewayStats && (
                <Box
                  sx={{
                    textAlign: { xs: 'left', sm: 'right' },
             
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.75)',
                      fontWeight: 500,
                    }}
                  >
                    {(() => {
                      const parts = [];
                      if (unifiGatewayStats.cpu != null) {
                        parts.push(`CPU ${unifiGatewayStats.cpu.toFixed(0)}%`);
                      }
                      if (unifiGatewayStats.cpuTemp != null) {
                        parts.push(`${unifiGatewayStats.cpuTemp.toFixed(0)}°C`);
                      }
                      if (unifiGatewayStats.ram != null) {
                        parts.push(`RAM ${unifiGatewayStats.ram.toFixed(0)}%`);
                      }
                      if (unifiGatewayStats.uptimeHours != null) {
                        parts.push(`${unifiGatewayStats.uptimeHours.toFixed(1)} h`);
                      }
                      const details = parts.length ? ` ${parts.join(' · ')}` : '';
                      return `CGU |${details}`;
                    })()}
                  </Typography>
                  
                  {!isMobile && (
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, justifyContent: 'flex-end' }}>
                      {[
                        { id: 'sensor.cloud_gateway_ultra_cloudflare_wan_latency', name: 'Cloudflare', icon: '/cloudflare_latency.svg' },
                        { id: 'sensor.cloud_gateway_ultra_google_wan_latency', name: 'Google', icon: '/google_latency.png' },
                        { id: 'sensor.cloud_gateway_ultra_microsoft_wan_latency', name: 'Microsoft', icon: '/ms_latency.png' }
                      ].map((ping) => (
                        <Box key={ping.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box component="img" src={ping.icon} sx={{ width: 14, height: 14, objectFit: 'contain' }} />
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600, fontSize: '0.7rem' }}>
                            {(() => {
                              const entity = haStates[ping.id];
                              if (!entity || entity.state == null || entity.state === 'unavailable') return 'n/a';
                              const val = Number(entity.state);
                              return `${Number.isFinite(val) ? val.toFixed(0) : entity.state} ms`;
                            })()}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Mobile latency indicators - compact row */}
                  {isMobile && (
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, justifyContent: 'flex-start' }}>
                      {[
                        { id: 'sensor.cloud_gateway_ultra_cloudflare_wan_latency', name: 'CF', icon: '/cloudflare_latency.svg' },
                        { id: 'sensor.cloud_gateway_ultra_google_wan_latency', name: 'G', icon: '/google_latency.png' },
                        { id: 'sensor.cloud_gateway_ultra_microsoft_wan_latency', name: 'MS', icon: '/ms_latency.png' }
                      ].map((ping) => (
                        <Box key={ping.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <Box component="img" src={ping.icon} sx={{ width: 12, height: 12, objectFit: 'contain' }} />
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600, fontSize: '0.65rem' }}>
                            {(() => {
                              const entity = haStates[ping.id];
                              if (!entity || entity.state == null || entity.state === 'unavailable') return 'n/a';
                              const val = Number(entity.state);
                              return `${Number.isFinite(val) ? val.toFixed(0) : entity.state} ms`;
                            })()}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {!isMobile && networkHistory.length > 0 && (
              <Box sx={{ mt: 1, mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mb: 0.5 }}>
                  {['1h', '4h', '12h', '24h'].map(p => (
                    <Button
                      key={p}
                      size="small"
                      variant={chartPeriod === p ? "contained" : "text"}
                      onClick={() => { setChartPeriod(p); fetchNetworkHistory(p); }}
                      sx={{ minWidth: 30, height: 20, fontSize: '0.65rem', p: 0, color: chartPeriod === p ? '#fff' : 'rgba(255,255,255,0.5)' }}
                    >
                      {p}
                    </Button>
                  ))}
                </Box>
                <Box sx={{ height: 60, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={networkHistory}>
                    <defs>
                      <linearGradient id="colorRx" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ff4081" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#ff4081" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorTx" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#00e676" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#00e676" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="time" hide />
                    <RechartsTooltip
                      contentStyle={{ backgroundColor: 'rgba(0,0,0,0.8)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                      itemStyle={{ fontSize: '0.7rem' }}
                      labelStyle={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}
                      labelFormatter={(time) => {
                        const date = new Date(time);
                        if (isNaN(date.getTime())) return 'Invalid time';
                        // Add 1 hour for wintertime (CET/CEST adjustment)
                        return new Date(date.getTime() + 3600000).toLocaleTimeString();
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="rx" 
                      stroke="#ff4081" 
                      fillOpacity={1} 
                      fill="url(#colorRx)" 
                      strokeWidth={2}
                      isAnimationActive={false}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="tx" 
                      stroke="#00e676" 
                      fillOpacity={1} 
                      fill="url(#colorTx)" 
                      strokeWidth={2}
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
                </Box>
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mt: 1,
              }}
            >
              {/* LAN column */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontWeight: 400,
                    letterSpacing: 0.3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Box component="span">LAN |</Box>
                  <Box
                    component="img"
                    src="/Ubiquity/USW%20Flex%202.5G.avif"
                    alt="USW Flex 2.5G"
                    sx={{
                      width: 28,
                      height: 28,
                      objectFit: 'contain',
                      borderRadius: '50%',
                    }}
                  />
                  <Box component="span">| P4 - TrueNAS</Box>
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#ffffff', fontWeight: 600, mt: 0.25 }}
                >
                  {unifiTrueNasPort?.link_speed_mbps
                    ? `${(unifiTrueNasPort.link_speed_mbps / 1000).toFixed(1)} GbE`
                    : '2.5 GbE'}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 0.75,
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ArrowDownward sx={{ fontSize: 18, color: '#ff4081' }} />
                    <Typography variant="body2" sx={{ color: '#ff4081', fontWeight: 600 }}>
                      {unifiTrueNasPort
                        ? `${(unifiTrueNasPort.rx_mbps_live ?? 0).toFixed(1)} Mbps`
                        : truenas.network_rx >= 1024
                          ? `${(truenas.network_rx / 1024).toFixed(1)} MB/s`
                          : `${(truenas.network_rx || 0).toFixed(0)} KB/s`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ArrowUpward sx={{ fontSize: 18, color: '#00e676' }} />
                    <Typography variant="body2" sx={{ color: '#00e676', fontWeight: 600 }}>
                      {unifiTrueNasPort
                        ? `${(unifiTrueNasPort.tx_mbps_live ?? 0).toFixed(1)} Mbps`
                        : truenas.network_tx >= 1024
                          ? `${(truenas.network_tx / 1024).toFixed(1)} MB/s`
                          : `${(truenas.network_tx || 0).toFixed(0)} KB/s`}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(
                      100,
                      ((unifiTrueNasPort ? unifiTrueNasPort.rx_mbps_live || 0 : 0) / 2500) * 100
                    )}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(90deg, #ff4081, #f50057)',
                        borderRadius: 3,
                      },
                    }}
                  />
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(
                      100,
                      ((unifiTrueNasPort ? unifiTrueNasPort.tx_mbps_live || 0 : 0) / 2500) * 100
                    )}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      mt: 0.75,
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(90deg, #00e676, #69f0ae)',
                        borderRadius: 3,
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* WAN column: auto-switches to WAN2 when failover is active */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontWeight: 400,
                    letterSpacing: 0.3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Box component="span">{unifiFailoverActive ? 'WAN2' : 'WAN1'} |</Box>
                  <Box
                    component="img"
                    src={unifiFailoverActive ? '/Ubiquity/orange.png' : '/Ubiquity/play.png'}
                    alt={unifiFailoverActive ? 'WAN2' : 'WAN1'}
                    sx={{
                      width: 28,
                      height: 28,
                      objectFit: 'contain',
                    }}
                  />
                  <Box component="span">| {unifiFailoverActive ? '4G Failover' : 'Public IP'}</Box>
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#ffffff', fontWeight: 600, mt: 0.25 }}
                >
                  {unifiWanDisplay?.ip || 'N/A'}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 0.75,
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ArrowDownward sx={{ fontSize: 18, color: '#ff4081' }} />
                    <Typography variant="body2" sx={{ color: '#ff4081', fontWeight: 600 }}>
                      {unifiWanDisplay ? `${(unifiWanDisplay.rx_mbps ?? 0).toFixed(1)} Mbps` : 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ArrowUpward sx={{ fontSize: 18, color: '#00e676' }} />
                    <Typography variant="body2" sx={{ color: '#00e676', fontWeight: 600 }}>
                      {unifiWanDisplay ? `${(unifiWanDisplay.tx_mbps ?? 0).toFixed(1)} Mbps` : 'N/A'}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, ((unifiWanDisplay?.rx_mbps || 0) / 800) * 100)}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(90deg, #ff4081, #f50057)',
                        borderRadius: 3,
                      },
                    }}
                  />
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, ((unifiWanDisplay?.tx_mbps || 0) / 40) * 100)}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      mt: 0.75,
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(90deg, #00e676, #69f0ae)',
                        borderRadius: 3,
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </UtilizationCard>
        </MobileScrollRow>

        {/* Integrated Storage Section */}
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1.5 }}>
            <Box component="img" src="/disk.png" sx={{ 
              width: 28, 
              height: 28, 
              filter: 'drop-shadow(0 0 8px rgba(156, 39, 176, 0.6))',
              '@media (max-width:600px)': {
                width: 18,
                height: 18,
              }
            }} />
            <Typography variant="h5" sx={{ 
              color: '#ffffff', 
              fontWeight: 700, 
              fontSize: '1.05rem',
              '@media (max-width:600px)': {
                fontSize: '0.95rem',
              }
            }}>Disk Pools</Typography>
          </Box>
          
          <DiskPoolsScrollRow>
            {poolData.map((pool, idx) => (
              <PoolCard key={idx} type={pool.type}>
                {/* Pool Header */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box>
                    <Typography variant="h6" sx={{ 
                      color: pool.type === 'protected' ? '#ffd700' : (pool.type === 'system' ? '#00d4ff' : '#ffffff'), 
                      fontWeight: 800,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      {pool.name}
                      {pool.type === 'protected' && <Bolt sx={{ fontSize: 18, color: '#ffd700' }} />}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 1 }}>
                      {pool.type === 'protected' ? 'RAID1 MIRROR' : (pool.type === 'system' ? 'OS BOOT DRIVE' : 'SINGLE DRIVE')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                    <Chip 
                      label={pool.health} 
                      size="small" 
                      icon={pool.health === 'ONLINE' ? <CheckCircleOutline /> : <ErrorOutline />}
                      sx={{ 
                        height: 24, 
                        fontWeight: 700,
                        bgcolor: pool.health === 'ONLINE' ? 'rgba(0,255,136,0.1)' : 'rgba(255,68,68,0.1)',
                        color: pool.health === 'ONLINE' ? '#00ff88' : '#ff4444',
                        border: '1px solid currentColor',
                        '& .MuiChip-icon': { color: 'inherit', fontSize: 16 }
                      }} 
                    />
                    {pool.type === 'protected' && (cpuRamData?.ram?.arc_gb > 0 || truenas.arc_usage > 0) && (
                      <Tooltip title={`ARC is actively boosting ${pool.name}`}>
                        <Chip 
                          label={`ARC BOOSTED: ${cpuRamData?.ram?.arc_gb?.toFixed(1) ?? (truenas.arc_usage / (1024**3)).toFixed(1)} GB`}
                          size="small"
                          icon={<Bolt sx={{ fontSize: 12 }} />}
                          sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            fontWeight: 800,
                            bgcolor: 'rgba(255, 215, 0, 0.15)',
                            color: '#ffd700',
                            border: '1px solid rgba(255, 215, 0, 0.4)',
                            boxShadow: '0 0 10px rgba(255, 215, 0, 0.1)',
                            '& .MuiChip-icon': { color: '#ffd700', marginLeft: '4px' },
                            '& .MuiChip-label': { paddingLeft: '4px' }
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                {/* Drives in this Pool */}
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                  {pool.drives.map(driveName => {
                    const drive = (truenas.disks || []).find(d => d.name === driveName) || { name: driveName, temperature: null, model: 'Unknown' };
                    const info = getDiskInfo(driveName);
                    const diskIO = (truenas.disk_io || {})[driveName] || {};
                    return (
                      <Tooltip key={driveName} title={`${drive.model} - ${drive.size_gb || '?'}GB${diskIO.read_human ? ` | R: ${diskIO.read_human}/s | W: ${diskIO.write_human}/s` : ''}`}>
                        <DiskBadge temp={drive.temperature} isProtected={pool.type === 'protected'}>
                          <Box component="img" src={info.icon} sx={{ width: 24, height: 24, mb: 0.5, filter: pool.type === 'protected' ? 'drop-shadow(0 0 5px rgba(255,215,0,0.5))' : 'none' }} />
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 700, fontSize: '0.65rem' }}>
                            {drive.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 800 }}>
                            {drive.temperature !== null ? `${drive.temperature}°` : '--°'}
                          </Typography>
                        </DiskBadge>
                      </Tooltip>
                    );
                  })}
                </Box>

                {/* Pool Disk I/O Stats */}
                {pool.drives.length > 0 && (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 600, display: 'block', mb: 1 }}>
                      DISK I/O (READ / WRITE)
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {pool.drives.map(driveName => {
                        const diskIO = (truenas.disk_io || {})[driveName] || {};
                        return (
                          <Box key={driveName} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, fontSize: '0.7rem', minWidth: 35 }}>
                              {driveName}:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#00ff88', fontWeight: 700, fontSize: '0.75rem' }}>
                              {diskIO.read_human || '--'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#ff4444', fontWeight: 700, fontSize: '0.75rem' }}>
                              {diskIO.write_human || '--'}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {/* Pool Usage */}
                {pool.type !== 'system' && (
                  <Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>
                        CAPACITY: {pool.allocated_human} / {((pool.size || 0) / (1024**4)).toFixed(1)}TB
                      </Typography>
                      <Typography variant="caption" sx={{ color: pool.allocated / pool.size > 0.85 ? '#ff4444' : '#ffffff', fontWeight: 700 }}>
                        {pool.size > 0 ? Math.round((pool.allocated / pool.size) * 100) : 0}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={pool.size > 0 ? (pool.allocated / pool.size) * 100 : 0} 
                      sx={{ 
                        height: 6, 
                        borderRadius: 3,
                        bgcolor: 'rgba(255,255,255,0.05)',
                        '& .MuiLinearProgress-bar': {
                          background: (theme) => pool.type === 'protected' 
                            ? 'linear-gradient(90deg, #ffd700, #ff8c00)' 
                            : (pool.type === 'system' ? theme.palette.primary.main : 'linear-gradient(90deg, #9c27b0, #e91e63)')
                        }
                      }}
                    />
                  </Box>
                )}
                {pool.type === 'system' && (
                  <Box sx={{ mt: 'auto', p: 1.5, bgcolor: 'rgba(0,212,255,0.05)', borderRadius: '12px', border: '1px dashed rgba(0,212,255,0.2)' }}>
                    <Typography variant="caption" sx={{ color: '#00d4ff', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Inventory sx={{ fontSize: 14 }} /> SYSTEM PARTITION - BOOT POOL
                    </Typography>
                  </Box>
                )}
              </PoolCard>
            ))}
          </DiskPoolsScrollRow>
        </Box>

        {/* Integrated Upcoming Jobs Section */}
        {upcomingTasks.length > 0 && (
          <Box sx={{ mt: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, gap: 1.5 }}>
              <AccessTime sx={{ 
                color: '#00d4ff', 
                fontSize: 28,
                '@media (max-width:600px)': {
                  fontSize: 18,
                }
              }} />
              <Typography variant="h5" sx={{ 
                color: '#ffffff', 
                fontWeight: 700, 
                fontSize: '1.05rem',
                '@media (max-width:600px)': {
                  fontSize: '0.95rem',
                }
              }}>Upcoming Maintenance</Typography>
            </Box>
            
            <Box
              ref={scrollContainerRef}
              sx={{
                display: 'flex',
                gap: 2,
                overflowX: 'auto',
                pb: 2,
                px: 0.5,
                width: '100%',
                userSelect: 'none',
                '&::-webkit-scrollbar': { height: '4px' },
                '&::-webkit-scrollbar-track': { background: 'rgba(255, 255, 255, 0.02)', borderRadius: '10px' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(0, 212, 255, 0.2)', borderRadius: '10px' },
              }}
            >
              {upcomingTasks.map((task) => (
                <Tooltip
                  key={task.id}
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{task.name}</Typography>
                      <Typography variant="caption" display="block">{task.description}</Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, color: '#00d4ff' }}>
                        Exact Time: {formatSafeDate(task.next_run)}
                      </Typography>
                    </Box>
                  }
                >
                  <UpcomingJobBadge statusColor={getStatusColor(task.status)}>
                    <Avatar
                      src={getServiceIcon(task.service)}
                      sx={{ 
                        width: 32, 
                        height: 32, 
                        p: 0.5, 
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '8px'
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap sx={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.2 }}>
                        {shortenTaskName(task.name)}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.2 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontWeight: 500 }}>
                          {formatTaskDate(task.next_run)}
                        </Typography>
                        <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                        <Typography variant="caption" sx={{ color: '#00ff88', fontWeight: 700 }}>
                          {task.next_run ? formatSafeDate(task.next_run, 'HH:mm') : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </UpcomingJobBadge>
                </Tooltip>
              ))}
            </Box>
          </Box>
        )}

        {/* Shutdown Confirmation Dialog */}
        <Dialog
          open={openShutdownDialog}
          onClose={handleShutdownCancel}
          PaperProps={{
            sx: {
              background: 'rgba(20, 20, 35, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 68, 68, 0.2)',
              borderRadius: '20px',
            }
          }}
        >
          <DialogTitle sx={{
            color: '#ff4444',
            fontWeight: 700,
            fontSize: '1.3rem',
            borderBottom: '1px solid rgba(255, 68, 68, 0.1)',
          }}>
            Confirm Server Shutdown
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
              Are you sure you want to shut down the TrueNAS Server?
            </Typography>
            <Typography sx={{ color: 'rgba(255, 68, 68, 0.8)', fontSize: '0.9rem', fontWeight: 500 }}>
              ⚠️ This action cannot be undone. All running services will be stopped.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ gap: 2, p: 2, borderTop: '1px solid rgba(255, 68, 68, 0.1)' }}>
            <Button
              onClick={handleShutdownCancel}
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleShutdownConfirm}
              sx={{
                backgroundColor: (theme) => theme.palette.error.main,
                color: (theme) => theme.palette.error.contrastText,
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.error.main,
                  filter: 'brightness(1.1)',
                  boxShadow: (theme) => `0 8px 24px ${theme.palette.error.main}4d`,
                }
              }}
            >
              Confirm Shutdown
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openHistoryDialog}
          onClose={() => setOpenHistoryDialog(false)}
          fullScreen={isMobile}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              background: 'rgba(20, 20, 35, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 235, 59, 0.2)',
              borderRadius: { xs: '16px', sm: '28px' },
              overflow: 'hidden',
              height: { xs: '85vh', sm: 'auto' },
              maxHeight: { xs: '85vh', sm: '90vh' },
              margin: { xs: 2, sm: 4 },
              display: 'flex',
              flexDirection: 'column'
            }
          }}
        >
          <DialogTitle sx={{
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'center', sm: 'center' },
            gap: 2,
            p: { xs: 2, sm: 3 },
            flexShrink: 0
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              <Box sx={{ 
                p: 1, 
                borderRadius: '12px', 
                bgcolor: 'rgba(255, 235, 59, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Bolt sx={{ color: '#ffeb3b' }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                  Power Usage History
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: { xs: 'none', sm: 'block' } }}>
                  TrueNAS Server energy consumption
                </Typography>
              </Box>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              width: { xs: '100%', sm: 'auto' },
              justifyContent: 'center'
            }}>
              {[
                { label: '24H', value: '24h' },
                { label: '7D', value: '7d' },
                { label: '30D', value: '30d' }
              ].map((period) => (
                <Button
                  key={period.value}
                  size="small"
                  onClick={() => handlePeriodChange(period.value)}
                  sx={{
                    minWidth: { xs: '70px', sm: '60px' },
                    borderRadius: '10px',
                    bgcolor: historyPeriod === period.value ? 'rgba(255, 235, 59, 0.2)' : 'transparent',
                    color: historyPeriod === period.value ? '#ffeb3b' : 'rgba(255, 255, 255, 0.5)',
                    border: '1px solid',
                    borderColor: historyPeriod === period.value ? '#ffeb3b' : 'rgba(255, 255, 255, 0.1)',
                    px: { xs: 1, sm: 2 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    '&:hover': {
                      bgcolor: 'rgba(255, 235, 59, 0.1)',
                      borderColor: '#ffeb3b'
                    }
                  }}
                >
                  {period.label}
                </Button>
              ))}
            </Box>
          </DialogTitle>
          <DialogContent sx={{ 
            p: { xs: 2, sm: 4 }, 
            height: { xs: 'calc(100% - 140px)', sm: '520px' }, 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden',
            flexGrow: 1,
            minHeight: 0
          }}>
            {historyLoading ? (
              <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                <LinearProgress sx={{ width: '50%', borderRadius: 2 }} />
              </Box>
            ) : historyData.length > 0 ? (
              <Box sx={{ flex: 1, width: '100%', mt: 2, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flex: 1, minHeight: { xs: 220, sm: 320 }, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={historyData} margin={{ top: 10, right: 10, left: 8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ffeb3b" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#ffeb3b" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                    <XAxis 
                      dataKey="time" 
                      stroke="rgba(255, 255, 255, 0.3)" 
                      fontSize={10}
                      tickLine={false}
                      axisLine={false}
                      minTickGap={historyPeriod === '24h' ? 30 : 60}
                    />
                    <YAxis 
                      stroke="rgba(255, 255, 255, 0.3)" 
                      fontSize={10}
                      tickLine={false}
                      axisLine={false}
                      unit="W"
                    />
                    <RechartsTooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(20, 20, 35, 0.9)', 
                        border: '1px solid rgba(255, 235, 59, 0.2)',
                        borderRadius: '12px',
                        color: '#fff',
                        fontSize: '11px',
                        padding: '8px'
                      }}
                      itemStyle={{ color: '#ffeb3b', padding: 0 }}
                      cursor={{ stroke: 'rgba(255, 235, 59, 0.2)', strokeWidth: 1 }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#ffeb3b" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorValue)" 
                      animationDuration={1000}
                    />
                  </AreaChart>
                </ResponsiveContainer>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  No historical data available for this period
                </Typography>
                <Button variant="outlined" size="small" onClick={() => fetchHistoryData(historyPeriod)}>
                  Retry
                </Button>
              </Box>
            )}
            
            {!historyLoading && historyData.length > 0 && (
              <Box sx={{ 
                mt: { xs: 2, sm: 3 }, 
                display: 'grid', 
                gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(5, 1fr)' },
                justifyContent: 'space-around', 
                p: { xs: 1.5, sm: 2 }, 
                bgcolor: 'rgba(255, 255, 255, 0.03)', 
                borderRadius: '16px',
                gap: 1
              }}>
                <Box textAlign="center" sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>AVG</Typography>
                  <Typography variant="h6" sx={{ color: '#ffeb3b', fontWeight: 700, fontSize: { xs: '0.8rem', sm: '1.25rem' } }}>
                    {(historyData.reduce((acc, curr) => acc + curr.value, 0) / historyData.length).toFixed(0)}W
                  </Typography>
                </Box>
                <Box textAlign="center" sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>MAX</Typography>
                  <Typography variant="h6" sx={{ color: '#ff4444', fontWeight: 700, fontSize: { xs: '0.8rem', sm: '1.25rem' } }}>
                    {Math.max(...historyData.map(d => d.value)).toFixed(0)}W
                  </Typography>
                </Box>
                <Box textAlign="center" sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>MIN</Typography>
                  <Typography variant="h6" sx={{ color: '#00ff88', fontWeight: 700, fontSize: { xs: '0.8rem', sm: '1.25rem' } }}>
                    {Math.min(...historyData.map(d => d.value)).toFixed(0)}W
                  </Typography>
                </Box>
                {historyData.length >= 2 && (() => {
                  const periodKwh = getPeriodKwhFromHistory(historyData, historyPeriod);
                  if (periodKwh == null) return null;
                  const days = getPeriodDays(historyPeriod);
                  const dailyKwh = periodKwh / days;
                  const monthlyKwh = periodKwh * (30 / days);
                  return (
                    <>
                      <Box textAlign="center" sx={{ minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>DAILY</Typography>
                        <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 700, fontSize: { xs: '0.8rem', sm: '1.25rem' } }}>
                          {dailyKwh < 1 ? dailyKwh.toFixed(2) : dailyKwh.toFixed(1)} kWh
                        </Typography>
                      </Box>
                      <Box textAlign="center" sx={{ minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>MONTHLY</Typography>
                        <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 700, fontSize: { xs: '0.8rem', sm: '1.25rem' } }}>
                          {monthlyKwh < 1 ? monthlyKwh.toFixed(2) : monthlyKwh.toFixed(1)} kWh
                        </Typography>
                      </Box>
                      {powerData?.yearly_usage && (
                        <Tooltip title="From Home Assistant sensor; accurate after a full year of data">
                          <Box textAlign="center" sx={{ minWidth: 0 }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>YEARLY</Typography>
                            <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 700, fontSize: { xs: '0.8rem', sm: '1.25rem' } }}>
                              {(parseFloat(powerData.yearly_usage.state) || 0).toFixed(1)} kWh
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                    </>
                  );
                })()}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ 
            p: { xs: 2, sm: 3 }, 
            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
            flexShrink: 0
          }}>
            <Button 
              onClick={() => setOpenHistoryDialog(false)}
              fullWidth
              sx={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                py: { xs: 1, sm: 1.5 }
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </StyledCard>
  );
};

export default StatusCard;